import { useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { AccountInfo, AuthenticationResult, EventType } from '@azure/msal-browser'

/**
 * Reactively returns the MSAL active account. Unlike msal-react's built-in
 * useAccount() — which only re-evaluates when inProgress changes — this hook
 * subscribes directly to ACTIVE_ACCOUNT_CHANGED and the post-login events,
 * so the UI updates the instant the user switches identities.
 */
export function useActiveAccount(): AccountInfo | null {
  const { instance } = useMsal()
  const [account, setAccount] = useState<AccountInfo | null>(() =>
    instance.getActiveAccount(),
  )

  useEffect(() => {
    const refresh = () => {
      const next = instance.getActiveAccount()
      setAccount(prev => (prev?.homeAccountId === next?.homeAccountId ? prev : next))
    }

    const callbackId = instance.addEventCallback(event => {
      if (event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED) {
        refresh()
        return
      }
      // main.tsx calls setActiveAccount in response to LOGIN_SUCCESS /
      // SSO_SILENT_SUCCESS, but the resulting ACTIVE_ACCOUNT_CHANGED event
      // only fires cross-tab in MSAL v3. Refresh defensively on the
      // sign-in events too so this hook stays in sync within the same tab.
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        (event.payload as AuthenticationResult)?.account
      ) {
        refresh()
      }
    })

    refresh()

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId)
    }
  }, [instance])

  return account
}
